import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import createGenericSlice from '@/store/reducers/sliceTemplate';
import { ResultState, ResultItem, ResultRecommendationsTypes, ResultDataState } from '@/store/types/resultTypes';
import type { RatingData } from '@/store/types/ratingTypes';
import type { PageDataTypes } from '@/store/types/pageTypes';
import type { AppDispatch, RootState } from '@/store/store';
import { apiUrl } from '@/api/axios';
import { getTranslation } from '@/core/utils/i18nHelper';
import { handleResponseError } from '@/core/utils/handleResponseError';
import { answersSent } from '@/store/reducers/testingSlice';
import { setCurrentRating } from '@/store/reducers/testRatingSlice';
import { setRatingFormEmail } from '@/store/reducers/testRatingFormSlice';
import API_ENDPOINTS from '@/api/apiConfig';
import { setPageData } from '@/store/reducers/pageSlice';
import { processResultsWithTemplates } from '@/core/utils/detectResultTemplate';
import { processResultHTML } from '@/core/utils/processResultHTML';

const initialResultData = {
	attempt: {
		attempt_key: '', // уникальный ключ попытки
		attempt_url: '', // для блока копировать результат
		results: [], // тексты стенайнов
		show_soft_404: false, // тест был модифицирован и результаты неактуальны
		template: '', // в будущем attempt.template
		avg_attempt_time: 0, // среднее время прохождения теста всеми пользователями
		duration_attempt_time: 0, // мое время прохождения теста
	},

	breadcrumbs: [],

	device: {
		country: '', // страна пользователя
		app: false, // приложение
	},

	test: {
		id: null,
		name: '', // название теста
		url: '', // для роутинга в будущем
		result_header: '', // результат header
		result_footer: '', // результат footer
		theme: '', // тема теста
	},

	adv: {
		show_adv: true,
	},

	blocks: {
		resultBreadcrumb: { show: false, content: '' }, // хлебные крошки в header
		resultBtnListTests: { show: false, content: '' }, // в список тестов
		resultHistory: { show: false, content: '' }, // история результатов
		resultHeader: { show: false, content: '' }, // описание результата
		resultHeaderPersonal: { show: false, content: '' }, // шапка описания результата
		resultFooter: { show: false, content: '' }, // описание результата в футере
		resultFooterPersonal: { show: false, content: '' }, // футер описания результата
		replaceResult: { show: false, content: '' }, // заменяем описание результата из контента
		socialLinks: { show: false, content: '' }, // соц сети ТМ
		appLinks: { show: false, content: '' }, // приложения ТМ
		resultRating: { show: false, content: '' }, // установка рейтинга и общий рейтинг теста
		recommendTests: { show: false, content: '' }, // рекомендованные тесты
		advFlocktory: { show: false, content: '' }, // реклама flocktory
		advResultTop: { show: false, content: '' }, // реклама
		advResultBottom: { show: false, content: '' }, // пока его нет
		resultRecommendForYou: { show: false, content: '' }, // рекомендации курсов и пр.
		resultRecommendForYouWidget: { show: false, content: '' }, // виджет рекомендаций
		resultRecommendBooks: { show: false, content: '' }, // рекомендации книг
		resultRecommendText: { show: false, content: '' }, // рекомендации текст
		forResultNeedSpecialist: { show: false, content: '' }, // не воспринимать результаты всерьез
		share: { show: false, content: '' },
	},
	user: {
		auth: false,
		email: '' // для авто вставки в форму рейтинга
	},
	widget: {
		is_widget: false,
		is_payed: false,
	},
	group: {
		is_group: false,
		is_payed: false,
	},
	recommend_for_you: { // рекомендуем лично вам
		recommends: [],
		litres: [],
		texts: []
	},
	incorrect_answers: [], // неправильные ответы с пояснением
}


const additionalState: Partial<ResultState> = {
    resultData: initialResultData,
	isResultShown: false,
	notFound: false,
};

// Дженерик-параметр — тип элементов базового `data: T[]`, которое для
// результата не используется (компоненты читают `resultData`/селекторы).
// Поэтому `unknown` — согласован с `ResultState extends GenericState<unknown>`.
const resultSlice = createGenericSlice<unknown>('result', additionalState);

const specificReducers = {
    setResult(state: ResultState, action: PayloadAction<ResultDataState>) {
        state.resultData = action.payload;
        // Успешно получили результат — снимаем флаг «не найдено».
        state.notFound = false;
    },

    resetResult(state: ResultState) {
        state.resultData = initialResultData;
        state.notFound = false;
    },

    // Бэкенд вернул 404 на /api/attempts/last: попытки нет. Не ошибка —
    // показываем empty state, поэтому отдельный флаг вместо state.error.
    setResultNotFound(state: ResultState) {
        state.notFound = true;
        state.isFetching = false;
    },
};

const combinedSlice = createSlice({
    name: 'result',
    initialState: resultSlice.getInitialState() as ResultState,
    reducers: {
        ...resultSlice.caseReducers,
        ...specificReducers,
    },
});

export const {
    fetchDataStart,
	fetchDataSuccess,
    fetchDataError,
    setResult,
    resetResult,
    setResultNotFound,
} = combinedSlice.actions;

export default combinedSlice.reducer;
export { combinedSlice as resultSlice };


/** HTML-несущий блок результата (флаг видимости + контент). */
interface RawResultBlock {
	show?: boolean;
	content?: string;
	[key: string]: unknown;
}

/**
 * Сырой ответ API результата (wire-shape). Типизируем только поля, которые
 * реально читаем/трансформируем здесь; остальное — через индексную сигнатуру,
 * чтобы spread сохранял неизвестные поля без `any`.
 */
export interface RawResultResponse {
	attempt?: {
		results?: ResultItem[];
		[key: string]: unknown;
	};
	blocks?: Record<string, RawResultBlock>;
	recommend_for_you?: ResultRecommendationsTypes;
	test?: {
		result_header?: string;
		result_footer?: string;
		[key: string]: unknown;
	};
	rating?: RatingData;
	page?: PageDataTypes;
	user?: { email?: string; [key: string]: unknown };
	[key: string]: unknown;
}

/**
 * Обрабатывает сырой ответ API результата и раскладывает данные по нужным slice-ам.
 *
 * Используется тремя thunk-ами: postResults, fetchResult, fetchResultByAttemptKey,
 * и сторис страницы результата (наполняют изолированный store мок-ответом).
 *
 * processResultHTML — преобразует относительные пути src="/storage/..." в абсолютные.
 * Работает ТОЛЬКО в DEV-режиме (когда фронт и бэк на разных хостах).
 * В production это no-op, относительные пути работают напрямую.
 */
export const processResultResponse = (resultResponse: RawResultResponse, dispatch: AppDispatch) => {
	// Прогоняем text каждого блока результата через processResultHTML (dev: пути к картинкам).
	// После этого processResultsWithTemplates проставляет template='default' и id по индексу
	// тем элементам, у которых эти поля не пришли с сервера.
	const resultsWithProcessedHTML = (resultResponse.attempt?.results || []).map((result) => ({
		...result,
		text: processResultHTML(result.text || ''),
	}));

	// Блоки — это флаги видимости + HTML-контент (resultHeader, advResultTop и т.д.).
	// Контент каждого блока тоже может содержать пути к картинкам — обрабатываем.
	const rawBlocks = resultResponse.blocks;
	const processedBlocks = rawBlocks ? Object.keys(rawBlocks).reduce<Record<string, RawResultBlock>>((acc, key) => {
		const block = rawBlocks[key];
		return {
			...acc,
			[key]: {
				...block,
				content: block.content ? processResultHTML(block.content) : block.content,
			},
		};
	}, {}) : rawBlocks;

	// Раздел "Рекомендуем лично вам": два массива с HTML-полями разной структуры.
	// recommends[].content — HTML карточки рекомендации
	// texts[].text — HTML текстовой рекомендации
	const rawRecommendations = resultResponse.recommend_for_you;
	const processedRecommendations = rawRecommendations ? {
		...rawRecommendations,
		recommends: rawRecommendations.recommends?.map((rec) => ({
			...rec,
			content: rec.content ? processResultHTML(rec.content) : rec.content,
		})),
		texts: rawRecommendations.texts?.map((text) => ({
			...text,
			text: text.text ? processResultHTML(text.text) : text.text,
		})),
	} : rawRecommendations;

	const processedResponse = {
		...resultResponse,
		attempt: {
			...resultResponse.attempt,
			results: processResultsWithTemplates(resultsWithProcessedHTML),
		},
		test: {
			...resultResponse.test,
			// result_header / result_footer — HTML шапки и подвала страницы результата
			result_header: processResultHTML(resultResponse.test?.result_header || ''),
			result_footer: processResultHTML(resultResponse.test?.result_footer || ''),
		},
		blocks: processedBlocks,
		recommend_for_you: processedRecommendations,
	};

	// Бэкенд присылает полный data-объект результата; здесь он уже прогнан
	// через processResultHTML/шаблоны. Wire-shape (RawResultResponse) намеренно
	// шире state-формы, поэтому фиксируем контракт явным приведением.
	dispatch(setResult(processedResponse as unknown as ResultDataState));

	// Рейтинг теста приходит опционально — диспатчится в отдельный slice testRating
	if (resultResponse.rating) {
		dispatch(
			setCurrentRating({
				rating: resultResponse.rating.rating,
				ratingPercent: resultResponse.rating.ratingPercent,
				isOwnAttempt: resultResponse.rating.isOwnAttempt,
				isUsrVoted: resultResponse.rating.isUsrVoted,
				cnt: resultResponse.rating.cnt ?? null,
			})
		);
	}

	// SEO-данные страницы (title, description и т.д.) — в отдельный slice page
	if (resultResponse.page) {
		dispatch(setPageData(resultResponse.page));
	}

	// Email пользователя предзаполняет форму оценки теста
	if (resultResponse.user && resultResponse.user.email) {
		dispatch(setRatingFormEmail(resultResponse.user.email));
	}

	dispatch(fetchDataSuccess());
};

export const postResults = (answers: unknown) => async (dispatch, getState) => {

	const { testId, widgetKey, groupKey, sid } = getState().testing;
	const rawDurations = getState().duration.durations;

	// Выравниваем длительности по ответам: ровно одно значение на каждый ответ,
	// дыры/недобор по длине → 0. durations — разреженный массив, индексируемый по
	// позиции вопроса (activeQuestion), тогда как answers растёт по числу отвеченных
	// (авто-ответ заполняет несколько вопросов разом, не «посещая» их). Без выравнивания
	// массивы расходятся, и зип на бэке (UserAnswerExportService) падал «Undefined array key».
	// Длительность — некритичная метрика, отсутствующее значение трактуем как 0.
	const answersLength = Array.isArray(answers) ? answers.length : 0;
	const durations = Array.from({ length: answersLength }, (_, i) => Number(rawDurations[i]) || 0);

	try {
		const response = await apiUrl.post(
			API_ENDPOINTS.results,
			{
				test_id: testId,
				durations: durations,
				answers: answers,
				...(widgetKey ? { wgt: widgetKey } : {}),
				...(groupKey ? { gkey: groupKey } : {}),
				...(sid ? { sid } : {}),
			},
			{
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
			}
		);

		const resultResponse = response.data.data;

		dispatch(answersSent(getTranslation('testing.success')));
		processResultResponse(resultResponse, dispatch);

	} catch (error) {
		console.error('Error while posting results:', error);
		handleResponseError(error, (data) => dispatch(fetchDataError(data)));
	}
};


export const fetchResult = (id: number) => async (dispatch, getState) => {
	try {
		const existingResult = getState().result.resultData;

		if (existingResult && Object.keys(existingResult).length > 0 && existingResult.attempt.attempt_key) {
			return; // Если данные уже загружены, повторный запрос не выполняется
		}

		dispatch(fetchDataStart());

		const { widgetKey, sid } = getState().testing;
		const query = { ...(widgetKey ? { wgt: widgetKey } : {}), ...(sid ? { sid } : {}) };
		const params = Object.keys(query).length ? { params: query } : {};
		const response = await apiUrl.get(`${API_ENDPOINTS.lastAttempt}${id}`, params);
		const resultResponse = response.data.data;
		processResultResponse(resultResponse, dispatch);
	} catch (error) {

		console.error('Error while fetching results:', error);
		// 404 — у пользователя нет последней попытки (частый кейс при
		// «протухшем» серверном флаге hasAttempts). Это не ошибка сети/сервера,
		// поэтому показываем empty state, а не ErrorWithReload.
		if ((error as { response?: { status?: number } })?.response?.status === 404) {
			dispatch(setResultNotFound());
			return;
		}
		handleResponseError(error, (data) => dispatch(fetchDataError(data)));
	}
};

export const fetchResultByAttemptKey = (attemptKey: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
	try {
		dispatch(fetchDataStart());
		const { widgetKey, sid } = getState().testing;
		const query = { ...(widgetKey ? { wgt: widgetKey } : {}), ...(sid ? { sid } : {}) };
		const params = Object.keys(query).length ? { params: query } : {};
		const response = await apiUrl.get(`/api/attempts/${attemptKey}`, params);
		const resultResponse = response.data.data;
		processResultResponse(resultResponse, dispatch);
	} catch (error) {
		console.error('Error while get result by attemptKey:', error);
		handleResponseError(error, (data) => dispatch(fetchDataError(data)));
	}
};